import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import type {
  ArchiveProvider,
  TelegramAccountView,
  TelegramGroupView,
  TelegramLoginResponse,
  TelegramMediaMode,
  TelegramMediaStorage,
} from '../../../shared/api.js';
import { api } from '../lib/api';
import { formatShortDateTime } from '../lib/format';

const QUERY_KEY = ['telegram-accounts'];

/** Phone → code → 2FA password, for a new account or to sign an existing one in again. */
function LoginWizard({
  account,
  label: initialLabel,
  needsApi,
  onClose,
}: {
  /** Signing an existing account in again: its id is fixed. */
  account?: string;
  label?: string;
  /** No account has api_id/api_hash yet: they are required. */
  needsApi: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [id, setId] = useState(account ?? '');
  const [label, setLabel] = useState(initialLabel ?? '');
  const [phone, setPhone] = useState('');
  const [apiId, setApiId] = useState('');
  const [apiHash, setApiHash] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [step, setStep] = useState<TelegramLoginResponse | undefined>();

  const advance = (next: TelegramLoginResponse) => {
    setStep(next);
    setCode('');
    setPassword('');
    if (next.step === 'done') void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
  };
  const start = useMutation({
    mutationFn: () =>
      api.telegramLoginStart({
        account: id.trim(),
        ...(label.trim() ? { label: label.trim() } : {}),
        phone,
        ...(apiId.trim() ? { apiId: Number(apiId) } : {}),
        ...(apiHash.trim() ? { apiHash: apiHash.trim() } : {}),
      }),
    onSuccess: advance,
  });
  const sendCode = useMutation({
    mutationFn: (login: string) => api.telegramLoginCode(login, code),
    onSuccess: advance,
  });
  const sendPassword = useMutation({
    mutationFn: (login: string) => api.telegramLoginPassword(login, password),
    onSuccess: advance,
  });
  const error = start.error ?? sendCode.error ?? sendPassword.error;

  const cancel = () => {
    if (step && step.step !== 'done')
      void api.telegramLoginCancel(step.login).catch(() => undefined);
    onClose();
  };

  if (step?.step === 'done') {
    return (
      <div className="card form">
        <h3>Готово</h3>
        <p>
          Аккаунт <strong>{step.account.label}</strong>
          {step.account.username && ` (@${step.account.username})`} подключён.
        </p>
        <div className="actions-row">
          <button type="button" className="btn btn--primary" onClick={onClose}>
            Закрыть
          </button>
        </div>
      </div>
    );
  }

  if (step?.step === 'code' || step?.step === 'password') {
    const isCode = step.step === 'code';
    const submit = (event: FormEvent) => {
      event.preventDefault();
      if (isCode) sendCode.mutate(step.login);
      else sendPassword.mutate(step.login);
    };
    const pending = sendCode.isPending || sendPassword.isPending;
    return (
      <form className="card form" onSubmit={submit}>
        <h3>{isCode ? 'Код из Telegram' : 'Пароль двухэтапной проверки'}</h3>
        {isCode ? (
          <>
            <p className="muted small">
              {step.viaApp
                ? 'Telegram прислал код в приложение на другом устройстве этого аккаунта.'
                : 'Telegram прислал код по SMS.'}
            </p>
            <label>
              Код
              <input
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                autoFocus
                required
              />
            </label>
          </>
        ) : (
          <label>
            Пароль {step.hint && <span className="muted small">(подсказка: {step.hint})</span>}
            <input
              type="password"
              autoComplete="off"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
              required
            />
          </label>
        )}
        {error && <p className="text-error small">{error.message}</p>}
        <div className="actions-row">
          <button type="submit" className="btn btn--primary" disabled={pending}>
            {pending ? 'Проверяю…' : 'Дальше'}
          </button>
          <button type="button" className="btn btn--ghost" onClick={cancel}>
            Отмена
          </button>
        </div>
      </form>
    );
  }

  const submit = (event: FormEvent) => {
    event.preventDefault();
    start.mutate();
  };
  return (
    <form className="card form" onSubmit={submit}>
      <h3>{account ? `Войти заново: ${initialLabel ?? account}` : 'Новый аккаунт Telegram'}</h3>
      <p className="muted small">
        Обычный аккаунт Telegram (не бот): он читает чаты для архива и может писать контактам. На
        телефон придёт код; код и пароль нигде не сохраняются, сохраняется только сессия
        (зашифрованной).
      </p>
      {!account && (
        <label>
          Ключ <span className="muted small">(латиница, например sleepman; не меняется)</span>
          <input
            value={id}
            onChange={(e) => setId(e.target.value.toLowerCase())}
            pattern="[a-z][a-z0-9_\-]{1,31}"
            required
          />
        </label>
      )}
      <label>
        Название
        <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder={id} />
      </label>
      <label>
        Телефон
        <input
          type="tel"
          autoComplete="off"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="+34600111222"
          required
        />
      </label>
      <details open={needsApi}>
        <summary className="small">
          api_id и api_hash {needsApi ? '' : '(можно не заполнять: возьмутся от другого аккаунта)'}
        </summary>
        <p className="muted small">my.telegram.org → API development tools.</p>
        <label>
          api_id
          <input
            inputMode="numeric"
            value={apiId}
            onChange={(e) => setApiId(e.target.value)}
            required={needsApi}
          />
        </label>
        <label>
          api_hash
          <input
            autoComplete="off"
            value={apiHash}
            onChange={(e) => setApiHash(e.target.value)}
            required={needsApi}
          />
        </label>
      </details>
      {error && <p className="text-error small">{error.message}</p>}
      <div className="actions-row">
        <button type="submit" className="btn btn--primary" disabled={start.isPending}>
          {start.isPending ? 'Отправляю код…' : 'Получить код'}
        </button>
        <button type="button" className="btn btn--ghost" onClick={cancel}>
          Отмена
        </button>
      </div>
    </form>
  );
}

const MEDIA_LABELS: Record<TelegramMediaMode, string> = {
  all: 'Все файлы',
  no_video: 'Без видео',
  none: 'Без файлов (голосовые расшифровываются)',
};

export function MediaSelect({
  value,
  disabled,
  onChange,
}: {
  value: TelegramMediaMode;
  disabled?: boolean;
  onChange: (mode: TelegramMediaMode) => void;
}) {
  return (
    <select
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value as TelegramMediaMode)}
    >
      {(Object.keys(MEDIA_LABELS) as TelegramMediaMode[]).map((mode) => (
        <option key={mode} value={mode}>
          {MEDIA_LABELS[mode]}
        </option>
      ))}
    </select>
  );
}

const STORAGE_LABELS: Record<TelegramMediaStorage, string> = {
  local: 'Диск сервера',
  s3: 'S3',
};

/** Where new files of a chat go. S3 needs S3_BUCKET and the keys on the server. */
export function StorageSelect({
  value,
  disabled,
  onChange,
}: {
  value: TelegramMediaStorage;
  disabled?: boolean;
  onChange: (storage: TelegramMediaStorage) => void;
}) {
  return (
    <select
      value={value}
      disabled={disabled}
      title="Куда класть новые файлы чата"
      onChange={(e) => onChange(e.target.value as TelegramMediaStorage)}
    >
      {(Object.keys(STORAGE_LABELS) as TelegramMediaStorage[]).map((storage) => (
        <option key={storage} value={storage}>
          {STORAGE_LABELS[storage]}
        </option>
      ))}
    </select>
  );
}

/**
 * The chats an account archives: which files each keeps, where they go and a pause. "Без видео"
 * keeps photos, documents, voice messages and video notes (they are transcribed). Applies to
 * new messages.
 */
export function ChatList({
  account,
  provider = 'telegram',
}: {
  account: string;
  provider?: ArchiveProvider;
}) {
  const queryClient = useQueryClient();
  const key = ['telegram-chats', provider, account];
  const chats = useQuery({ queryKey: key, queryFn: () => api.telegramChats(account, provider) });
  const update = useMutation({
    mutationFn: ({
      id,
      patch,
    }: {
      id: number;
      patch: Parameters<typeof api.telegramChatUpdate>[1];
    }) => api.telegramChatUpdate(id, patch),
    onSettled: () => queryClient.invalidateQueries({ queryKey: key }),
  });
  return (
    <div className="tg-chats">
      <h4>Чаты</h4>
      <p className="muted small">
        Файлы: «Без видео» — фото, документы, голосовые и кружки качаются, видео и гифки нет.
        Хранилище: диск сервера или S3 (уже сохранённые файлы остаются где были). Действует для
        новых сообщений.
      </p>
      {chats.isError && <p className="text-error small">{chats.error.message}</p>}
      <ul className="tg-chats__list">
        {chats.data?.chats.map((chat) => (
          <li key={chat.id} className="tg-chats__row">
            <div className="tg-chats__main">
              <span className="tg-chats__title">{chat.title}</span>
              <span className="muted small">
                {chat.messages} сообщ.
                {chat.lastMessageAt && ` · последнее ${formatShortDateTime(chat.lastMessageAt)}`}
                {!chat.resolved && ' · ещё не найден'}
              </span>
              {chat.lastError && <span className="text-error small">{chat.lastError}</span>}
            </div>
            <div className="tg-chats__controls">
              <MediaSelect
                value={chat.media}
                disabled={update.isPending}
                onChange={(media) => update.mutate({ id: chat.id, patch: { media } })}
              />
              <StorageSelect
                value={chat.storage}
                disabled={update.isPending}
                onChange={(storage) => update.mutate({ id: chat.id, patch: { storage } })}
              />
              {chat.mediaRefetch ? (
                <span
                  className="pill pill--warn"
                  title="Архив качает файлы сообщений, у которых их не было"
                >
                  докачиваю файлы…
                </span>
              ) : (
                <button
                  type="button"
                  className="btn btn--small btn--ghost"
                  disabled={update.isPending}
                  title="Скачать файлы сообщений, которые были заархивированы без файла (режим «без файлов», слишком большие, ошибки), в текущее хранилище"
                  onClick={() => update.mutate({ id: chat.id, patch: { mediaRefetch: true } })}
                >
                  Докачать файлы
                </button>
              )}
              <label className="calendar-list__toggle small">
                <input
                  type="checkbox"
                  checked={!chat.paused}
                  disabled={update.isPending}
                  onChange={(e) =>
                    update.mutate({ id: chat.id, patch: { paused: !e.target.checked } })
                  }
                />
                архив
              </label>
            </div>
          </li>
        ))}
      </ul>
      {update.isError && <p className="text-error small">{update.error.message}</p>}
    </div>
  );
}

/** One group the account is in, not archived yet: which files, the history, a button. */
function GroupRow({
  account,
  provider,
  group,
  defaultMedia,
  defaultStorage,
  onAdded,
}: {
  account: string;
  provider: ArchiveProvider;
  group: TelegramGroupView;
  defaultMedia: TelegramMediaMode;
  defaultStorage: TelegramMediaStorage;
  onAdded: () => void;
}) {
  const [media, setMedia] = useState<TelegramMediaMode>(defaultMedia);
  const [storage, setStorage] = useState<TelegramMediaStorage>(defaultStorage);
  const [history, setHistory] = useState(false);
  const add = useMutation({
    mutationFn: () =>
      api.telegramChatAdd({
        provider,
        account,
        peerId: group.peerId,
        fromNow: !history,
        media,
        storage,
      }),
    onSuccess: onAdded,
  });
  return (
    <li className="tg-chats__row">
      <div className="tg-chats__main">
        <span className="tg-chats__title">{group.title}</span>
        <span className="muted small">{group.kind === 'channel' ? 'канал' : 'группа'}</span>
        {add.isError && <span className="text-error small">{add.error.message}</span>}
      </div>
      <div className="tg-chats__controls">
        <MediaSelect value={media} disabled={add.isPending} onChange={setMedia} />
        <StorageSelect value={storage} disabled={add.isPending} onChange={setStorage} />
        <label className="calendar-list__toggle small">
          <input
            type="checkbox"
            checked={history}
            disabled={add.isPending}
            onChange={(e) => setHistory(e.target.checked)}
          />
          с историей
        </label>
        <button
          type="button"
          className="btn btn--small btn--primary"
          disabled={add.isPending}
          onClick={() => add.mutate()}
        >
          {add.isPending ? '…' : 'Архивировать'}
        </button>
      </div>
    </li>
  );
}

/**
 * The groups and channels the account is in, asked from Telegram on request: start archiving
 * one with the files chosen ("с историей": the whole history, else from now on).
 */
export function GroupPicker({
  account,
  provider = 'telegram',
  defaultMedia,
  defaultStorage,
}: {
  account: string;
  provider?: ArchiveProvider;
  defaultMedia: TelegramMediaMode;
  defaultStorage: TelegramMediaStorage;
}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const key = ['telegram-groups', provider, account];
  const groups = useQuery({
    queryKey: key,
    queryFn: () => api.telegramGroups(account, provider),
    enabled: open,
    staleTime: 60_000,
  });
  const added = () => {
    void queryClient.invalidateQueries({ queryKey: key });
    void queryClient.invalidateQueries({ queryKey: ['telegram-chats', provider, account] });
    void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    void queryClient.invalidateQueries({ queryKey: ['whatsapp-accounts'] });
  };
  const free = groups.data?.groups.filter((g) => g.chat === undefined) ?? [];
  if (!open) {
    return (
      <div className="tg-chats">
        <button type="button" className="btn btn--small" onClick={() => setOpen(true)}>
          Группы аккаунта…
        </button>
      </div>
    );
  }
  return (
    <div className="tg-chats">
      <h4>Группы аккаунта</h4>
      <p className="muted small">
        Где состоит аккаунт. «Архивировать» — начать собирать группу; без «с историей» — только
        новые сообщения.
        {provider === 'whatsapp' &&
          ' В WhatsApp история запрашивается у телефона по частям после первого нового сообщения.'}
      </p>
      {groups.isPending && (
        <p className="muted small">
          Спрашиваю у {provider === 'whatsapp' ? 'WhatsApp' : 'Telegram'}…
        </p>
      )}
      {groups.isError && <p className="text-error small">{groups.error.message}</p>}
      {groups.data && free.length === 0 && (
        <p className="muted small">Все группы аккаунта уже в архиве (или групп нет).</p>
      )}
      <ul className="tg-chats__list">
        {free.map((group) => (
          <GroupRow
            key={group.peerId}
            account={account}
            provider={provider}
            group={group}
            defaultMedia={defaultMedia}
            defaultStorage={defaultStorage}
            onAdded={added}
          />
        ))}
      </ul>
    </div>
  );
}

function AccountCard({
  account,
  archiveHere,
  onSignIn,
}: {
  account: TelegramAccountView;
  archiveHere: boolean;
  onSignIn: () => void;
}) {
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: QUERY_KEY });
  const update = useMutation({
    mutationFn: (patch: Parameters<typeof api.telegramAccountUpdate>[1]) =>
      api.telegramAccountUpdate(account.id, patch),
    onSettled: refresh,
  });
  const signOut = useMutation({
    mutationFn: () => api.telegramAccountSignOut(account.id),
    onSettled: refresh,
  });
  const remove = useMutation({
    mutationFn: () => api.telegramAccountRemove(account.id),
    onSettled: refresh,
  });
  const [admins, setAdmins] = useState(account.admins.join(', '));
  const adminList = admins
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  const adminsChanged = adminList.join(',') !== account.admins.join(',');

  const pill = account.lastError
    ? { cls: 'pill--error', text: 'ошибка' }
    : account.signedIn
      ? { cls: 'pill--ok', text: 'вошёл' }
      : { cls: 'pill--warn', text: 'не вошёл' };
  const error = update.error ?? signOut.error ?? remove.error;

  return (
    <div className="card">
      <div className="card__header">
        <h3>
          {account.label} <span className="muted small">· {account.id}</span>
        </h3>
        <span className={`pill ${pill.cls}`}>{pill.text}</span>
      </div>
      <dl className="facts">
        <dt>Аккаунт</dt>
        <dd>
          {account.displayName ?? '—'}
          {account.username && ` (@${account.username})`}
          {account.telegramUserId && <span className="muted"> · id {account.telegramUserId}</span>}
        </dd>
        {account.phone && (
          <>
            <dt>Телефон</dt>
            <dd>{account.phone}</dd>
          </>
        )}
        <dt>Архив</dt>
        <dd>
          {account.chats} чатов, {account.messages} сообщений
          {account.archiving
            ? ' · идёт'
            : account.archive && account.signedIn && !archiveHere
              ? ' · на этом сервере архив выключен'
              : ''}
        </dd>
        {account.signedInAt && (
          <>
            <dt>Вход</dt>
            <dd>{formatShortDateTime(account.signedInAt)}</dd>
          </>
        )}
        {account.fromEnv && (
          <>
            <dt>Сессия</dt>
            <dd>
              из <code>.env</code> (TELEGRAM_SESSION): без <code>SECRETS_KEY</code> её нельзя
              сохранить в базу
            </dd>
          </>
        )}
        {account.lastError && (
          <>
            <dt>Ошибка</dt>
            <dd className="text-error">{account.lastError}</dd>
          </>
        )}
      </dl>

      <div className="toggles">
        <label className="calendar-list__toggle">
          <input
            type="checkbox"
            checked={account.archive}
            disabled={update.isPending}
            onChange={(e) => update.mutate({ archive: e.target.checked })}
          />
          Архивировать чаты
        </label>
        <label className="calendar-list__toggle">
          <input
            type="checkbox"
            checked={account.send}
            disabled={update.isPending}
            onChange={(e) => {
              if (
                !e.target.checked ||
                window.confirm(
                  'Разрешить агенту отправлять сообщения от этого аккаунта контактам? ' +
                    'Каждая отправка всё равно требует подтверждения.',
                )
              ) {
                update.mutate({ send: e.target.checked });
              }
            }}
          />
          Разрешить отправку сообщений
        </label>
      </div>

      <label className="small">
        Админы команд в чатах <span className="muted">(id в Telegram через запятую)</span>
        <div className="inline-form">
          <input value={admins} onChange={(e) => setAdmins(e.target.value)} />
          <button
            type="button"
            className="btn btn--small"
            disabled={!adminsChanged || update.isPending}
            onClick={() => update.mutate({ admins: adminList })}
          >
            Сохранить
          </button>
        </div>
      </label>

      <label className="small media-default">
        Файлы новых чатов <span className="muted">(в том числе добавленных командой record)</span>
        <MediaSelect
          value={account.mediaDefault}
          disabled={update.isPending}
          onChange={(mediaDefault) => update.mutate({ mediaDefault })}
        />
      </label>
      <label className="small media-default">
        Хранилище новых чатов <span className="muted">(диск сервера или S3)</span>
        <StorageSelect
          value={account.storageDefault}
          disabled={update.isPending}
          onChange={(storageDefault) => update.mutate({ storageDefault })}
        />
      </label>

      {account.chats > 0 && <ChatList account={account.id} />}
      {account.signedIn && (
        <GroupPicker
          account={account.id}
          defaultMedia={account.mediaDefault}
          defaultStorage={account.storageDefault}
        />
      )}

      {error && <p className="text-error small">{error.message}</p>}
      <div className="actions-row">
        <button type="button" className="btn" onClick={onSignIn}>
          {account.signedIn ? 'Войти заново' : 'Войти'}
        </button>
        {account.signedIn && (
          <button
            type="button"
            className="btn btn--ghost"
            disabled={signOut.isPending}
            onClick={() => {
              if (
                window.confirm(
                  'Выйти из аккаунта? Сессия будет закрыта в Telegram, архив останется.',
                )
              ) {
                signOut.mutate();
              }
            }}
          >
            Выйти
          </button>
        )}
        {account.chats === 0 && account.messages === 0 && (
          <button
            type="button"
            className="btn btn--ghost"
            disabled={remove.isPending}
            onClick={() => {
              if (window.confirm(`Удалить аккаунт ${account.label}?`)) remove.mutate();
            }}
          >
            Удалить
          </button>
        )}
      </div>
    </div>
  );
}

/** Settings → Telegram (owner only): the accounts that archive chats and send messages. */
export function TelegramSection() {
  const query = useQuery({ queryKey: QUERY_KEY, queryFn: api.telegramAccounts });
  const [wizard, setWizard] = useState<{ account?: string; label?: string } | undefined>();
  const data = query.data;
  const needsApi = !data?.accounts.some((a) => a.setUp);

  return (
    <section>
      <h2>Telegram</h2>
      <p className="muted">
        Аккаунты, которые собирают архив чатов и могут писать контактам. Каждое сообщение в архиве
        помнит, какой аккаунт его собрал.
      </p>
      {query.isPending && <p className="muted">Загрузка…</p>}
      {query.isError && <p className="text-error">{query.error.message}</p>}
      {data && !data.canStoreSecrets && (
        <p className="notice notice--error">
          На сервере не задан <code>SECRETS_KEY</code>: сессии нельзя сохранить. Добавьте его в{' '}
          <code>.env</code> (<code>openssl rand -hex 32</code>) и перезапустите сервер.
        </p>
      )}
      {data && !data.archiveHere && (
        <p className="muted small">
          Архив на этом сервере выключен (<code>TELEGRAM_ARCHIVE</code>): аккаунты здесь только
          читают и отправляют.
        </p>
      )}
      {wizard ? (
        <LoginWizard
          {...(wizard.account ? { account: wizard.account } : {})}
          {...(wizard.label ? { label: wizard.label } : {})}
          needsApi={needsApi}
          onClose={() => setWizard(undefined)}
        />
      ) : (
        data?.canStoreSecrets && (
          <div className="actions-row">
            <button type="button" className="btn btn--primary" onClick={() => setWizard({})}>
              Добавить аккаунт
            </button>
          </div>
        )
      )}
      {data && data.accounts.length === 0 && <p className="muted">Аккаунтов пока нет.</p>}
      <div className="stack">
        {data?.accounts.map((account) => (
          <AccountCard
            key={account.id}
            account={account}
            archiveHere={data.archiveHere}
            onSignIn={() => setWizard({ account: account.id, label: account.label })}
          />
        ))}
      </div>
    </section>
  );
}
